import AttachFileIcon from '@mui/icons-material/AttachFile'
import CancelIcon from '@mui/icons-material/Cancel'
import CheckBoxIcon from '@mui/icons-material/CheckBox'
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank'
import CloudUploadIcon from '@mui/icons-material/CloudUpload'
import DeleteIcon from '@mui/icons-material/Delete'
import LinkOffIcon from '@mui/icons-material/LinkOff'
import PlayArrowIcon from '@mui/icons-material/PlayArrow'
import {
  Box,
  Button,
  Card,
  CardActions,
  CardMedia,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid2 as Grid,
  IconButton,
  Paper,
  Slider,
  Typography,
} from '@mui/material'
import { useAtomValue } from 'jotai'
import React, { useRef, useState } from 'react'

import { currentUserAtom } from '../../domains/user/AtomUsers'
import VideoService from '../../services/VideoService'
import useBar from '../common/Bar'
import FullscreenMediaViewer from '../common/FullscreenMediaViewer'

/**
 * VideoManagement - Displays user's videos in a grid layout with add/delete functionality
 * Features video preview and auto-generates poster from video frame
 * @param {Object} props
 * @param {Array} props.videos - Array of video objects with {resourceId, url, coverImageUrl} or just URL strings
 * @param {Function} props.onVideosChange - Callback when videos are added/deleted
 * @param {boolean} [props.editable=true] - Whether user can add/delete videos
 */
const VideoManagement = ({ videos = [], onVideosChange, editable = true }) => {
  const [openUploadDialog, setOpenUploadDialog] = useState(false)
  const [selectedFiles, setSelectedFiles] = useState([]) // Files selected but not uploaded yet
  const [uploadedVideos, setUploadedVideos] = useState([]) // Videos uploaded but not confirmed
  const [isUploading, setIsUploading] = useState(false)
  const [isConfirming, setIsConfirming] = useState(false)
  const [deletingVideoId, setDeletingVideoId] = useState(null)
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false)
  const [videoToDelete, setVideoToDelete] = useState(null)
  const [unlinkConfirmOpen, setUnlinkConfirmOpen] = useState(false)
  const [videoToUnlink, setVideoToUnlink] = useState(null)
  const [isDragging, setIsDragging] = useState(false)
  const [openAttachDialog, setOpenAttachDialog] = useState(false)
  const [existingVideos, setExistingVideos] = useState([])
  const [selectedExistingVideos, setSelectedExistingVideos] = useState([])
  const [loadingExistingVideos, setLoadingExistingVideos] = useState(false)
  const [attachingVideos, setAttachingVideos] = useState(false)
  const [currentVideoPreview, setCurrentVideoPreview] = useState(null) // Currently previewing video for poster selection
  const [viewerOpen, setViewerOpen] = useState(false)
  const [viewerInitialIndex, setViewerInitialIndex] = useState(0)
  const fileInputRef = useRef(null)
  const videoRef = useRef(null) // For video playback and poster generation
  const canvasRef = useRef(null) // For generating poster from video frame
  const bar = useBar()
  const user = useAtomValue(currentUserAtom)

  // Convert videos to consistent format
  const normalizedVideos = videos.map((vid, index) => {
    if (typeof vid === 'string') {
      return { url: vid, id: `vid-${index}` }
    }
    return {
      url: vid.url || vid.r2Url,
      coverUrl: vid.coverImageUrl || vid.coverUrl,
      id: vid.resourceId || `vid-${index}`
    }
  })

  const handleOpenUploadDialog = () => {
    setOpenUploadDialog(true)
    setSelectedFiles([])
    setUploadedVideos([])
  }

  const handleCloseUploadDialog = () => {
    // Clean up uploaded videos if dialog is closed without confirming
    if (uploadedVideos.length > 0) {
      handleCancelAllUploads()
    }
    // Clean up preview URLs
    selectedFiles.forEach((file) => {
      if (file.previewUrl) {
        URL.revokeObjectURL(file.previewUrl)
      }
      if (file.posterUrl) {
        URL.revokeObjectURL(file.posterUrl)
      }
    })
    setOpenUploadDialog(false)
    setSelectedFiles([])
    setUploadedVideos([])
    setCurrentVideoPreview(null)
  }

  /**
   * Validate a single video file
   */
  const validateFile = (file) => {
    const allowedTypes = ['video/mp4', 'video/quicktime', 'video/x-msvideo', 'video/webm']
    if (!allowedTypes.includes(file.type)) {
      throw new Error(`Invalid file type: ${file.name}. Only MP4, MOV, AVI, and WEBM are allowed.`)
    }

    const maxSize = 500 * 1024 * 1024 // 500MB
    if (file.size > maxSize) {
      throw new Error(`File size exceeds 500MB: ${file.name}`)
    }

    return true
  }

  /**
   * Generate a poster image from video at specific time
   */
  const generatePosterFromVideo = (videoElement, _currentTime = 0) => {
    return new Promise((resolve, reject) => {
      try {
        const canvas = canvasRef.current
        if (!canvas) {
          reject(new Error('Canvas element not available'))
          return
        }

        // Set canvas dimensions to match video
        canvas.width = videoElement.videoWidth
        canvas.height = videoElement.videoHeight

        // Draw current video frame to canvas
        const ctx = canvas.getContext('2d')
        ctx.drawImage(videoElement, 0, 0, canvas.width, canvas.height)

        // Convert canvas to blob
        canvas.toBlob(
          (blob) => {
            if (blob) {
              const posterFile = new File([blob], 'poster.jpg', { type: 'image/jpeg' })
              const posterUrl = URL.createObjectURL(blob)
              resolve({ posterFile, posterUrl })
            } else {
              reject(new Error('Failed to generate poster'))
            }
          },
          'image/jpeg',
          0.9
        )
      } catch (error) {
        reject(error)
      }
    })
  }

  /**
   * Handle file selection (from input or drop)
   */
  const handleFileSelect = async (files) => {
    const fileArray = Array.from(files)
    const validFiles = []
    const errors = []

    for (const file of fileArray) {
      try {
        validateFile(file)
        const previewUrl = URL.createObjectURL(file)

        // Create a temporary video element to generate poster
        const tempVideo = document.createElement('video')
        tempVideo.src = previewUrl
        tempVideo.muted = true

        // Wait for video metadata to load
        await new Promise((resolve, reject) => {
          tempVideo.onloadedmetadata = resolve
          tempVideo.onerror = reject
        })

        // Seek to 1 second (or middle of video if shorter than 2 seconds)
        const seekTime = Math.min(1, tempVideo.duration / 2)
        tempVideo.currentTime = seekTime

        // Wait for seek to complete
        await new Promise((resolve) => {
          tempVideo.onseeked = resolve
        })

        // Generate initial poster
        const { posterFile, posterUrl } = await generatePosterFromVideo(tempVideo)

        validFiles.push({
          file,
          previewUrl,
          posterFile,
          posterUrl,
          currentTime: seekTime,
          duration: tempVideo.duration,
          id: `file-${Date.now()}-${Math.random()}`,
        })
      } catch (error) {
        errors.push(error.message)
      }
    }

    if (errors.length > 0) {
      bar.error(`Some files were rejected: ${errors.join(', ')}`)
    }

    if (validFiles.length > 0) {
      setSelectedFiles((prev) => [...prev, ...validFiles])
      bar.info(`${validFiles.length} video(s) selected. Poster images generated automatically.`)
    }
  }

  /**
   * Remove a selected file from preview
   */
  const handleRemoveSelectedFile = (fileId) => {
    setSelectedFiles((prev) => {
      const fileToRemove = prev.find((f) => f.id === fileId)
      if (fileToRemove) {
        if (fileToRemove.previewUrl) {
          URL.revokeObjectURL(fileToRemove.previewUrl)
        }
        if (fileToRemove.posterUrl) {
          URL.revokeObjectURL(fileToRemove.posterUrl)
        }
      }
      return prev.filter((f) => f.id !== fileId)
    })
  }

  /**
   * Open video preview for poster time selection
   */
  const handleOpenVideoPreview = (fileItem) => {
    setCurrentVideoPreview(fileItem)
  }

  /**
   * Update poster image when user scrubs through video
   */
  const handlePosterTimeChange = async (event, newValue) => {
    if (!currentVideoPreview || !videoRef.current) return

    const video = videoRef.current
    video.currentTime = newValue

    // Wait for seek to complete
    await new Promise((resolve) => {
      video.onseeked = resolve
    })

    try {
      const { posterFile, posterUrl } = await generatePosterFromVideo(video, newValue)

      // Clean up old poster URL
      if (currentVideoPreview.posterUrl) {
        URL.revokeObjectURL(currentVideoPreview.posterUrl)
      }

      // Update the file item with new poster
      setSelectedFiles((prev) =>
        prev.map((f) =>
          f.id === currentVideoPreview.id
            ? { ...f, posterFile, posterUrl, currentTime: newValue }
            : f
        )
      )

      setCurrentVideoPreview((prev) => ({
        ...prev,
        posterFile,
        posterUrl,
        currentTime: newValue,
      }))
    } catch (error) {
      console.error('Failed to update poster:', error)
      bar.error('Failed to update poster image')
    }
  }

  /**
   * Handle file input change
   */
  const handleFileInputChange = (event) => {
    if (event.target.files && event.target.files.length > 0) {
      handleFileSelect(event.target.files)
    }
    // Reset file input
    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }
  }

  /**
   * Handle drag events
   */
  const handleDragEnter = (e) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragging(true)
  }

  const handleDragLeave = (e) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragging(false)
  }

  const handleDragOver = (e) => {
    e.preventDefault()
    e.stopPropagation()
  }

  const handleDrop = (e) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragging(false)

    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      handleFileSelect(e.dataTransfer.files)
    }
  }

  /**
   * Upload all selected files
   */
  const handleUploadFiles = async () => {
    if (selectedFiles.length === 0) {
      return
    }

    setIsUploading(true)

    try {
      const uploadPromises = selectedFiles.map(async (fileItem) => {
        const timestamp = Date.now()
        const randomId = Math.random().toString(36).substring(7)
        const videoData = await VideoService.uploadVideo(
          fileItem.file,
          fileItem.posterFile,
          `video-${timestamp}-${randomId}`
        )
        return videoData
      })

      const uploadedData = await Promise.all(uploadPromises)
      setUploadedVideos(uploadedData)

      // Clean up preview URLs
      selectedFiles.forEach((file) => {
        if (file.previewUrl) {
          URL.revokeObjectURL(file.previewUrl)
        }
        if (file.posterUrl) {
          URL.revokeObjectURL(file.posterUrl)
        }
      })
      setSelectedFiles([])

      bar.success(`${uploadedData.length} video(s) uploaded successfully. Click "Confirm" to add to your gallery.`)
    } catch (error) {
      console.error('Failed to upload videos:', error)
      bar.error(error.message || 'Failed to upload videos')
    } finally {
      setIsUploading(false)
    }
  }

  /**
   * Confirm adding uploaded videos to gallery
   */
  const handleConfirmUpload = async () => {
    if (uploadedVideos.length === 0) {
      return
    }

    setIsConfirming(true)

    try {
      const newVideos = uploadedVideos.map((videoData) => ({
        resourceId: videoData.resourceId,
        url: videoData.r2Url,
        coverImageUrl: videoData.coverImageUrl,
      }))

      const updatedVideos = [...videos, ...newVideos]

      if (onVideosChange) {
        await onVideosChange(updatedVideos)
      }

      bar.success(`${newVideos.length} video(s) added to gallery successfully`)
      setUploadedVideos([])
      setOpenUploadDialog(false)
    } catch (error) {
      console.error('Failed to add videos:', error)
      bar.error(error.message || 'Failed to add videos to gallery')
    } finally {
      setIsConfirming(false)
    }
  }

  /**
   * Cancel all uploads and delete uploaded videos
   */
  const handleCancelAllUploads = async () => {
    if (uploadedVideos.length === 0) {
      return
    }

    try {
      const deletePromises = uploadedVideos.map((videoData) => VideoService.deleteVideo(videoData.resourceId))
      await Promise.all(deletePromises)
      setUploadedVideos([])
      bar.info('Uploaded videos have been cancelled')
    } catch (error) {
      console.error('Failed to delete uploaded videos:', error)
      setUploadedVideos([])
    }
  }

  /**
   * Open delete confirmation dialog
   */
  const handleDeleteClick = (video, index) => {
    setVideoToDelete({ video, index })
    setDeleteConfirmOpen(true)
  }

  /**
   * Close delete confirmation dialog
   */
  const handleDeleteCancel = () => {
    setDeleteConfirmOpen(false)
    setVideoToDelete(null)
  }

  /**
   * Open unlink confirmation dialog
   */
  const handleUnlinkClick = (video, index) => {
    setVideoToUnlink({ video, index })
    setUnlinkConfirmOpen(true)
  }

  /**
   * Close unlink confirmation dialog
   */
  const handleUnlinkCancel = () => {
    setUnlinkConfirmOpen(false)
    setVideoToUnlink(null)
  }

  /**
   * Delete a video from the gallery after confirmation
   * This will remove the video from the list AND delete it from the server
   */
  const handleDeleteConfirm = async () => {
    if (!videoToDelete) return

    const { video, index } = videoToDelete
    setDeletingVideoId(video.id)

    try {
      const originalVideo = videos[index]
      let resourceId = null

      if (typeof originalVideo === 'object' && originalVideo.resourceId) {
        resourceId = originalVideo.resourceId
      }

      if (resourceId) {
        try {
          await VideoService.deleteVideo(resourceId)
        } catch (deleteError) {
          console.warn('Failed to delete video from server:', deleteError)
        }
      }

      const updatedVideos = videos.filter((_, i) => i !== index)

      if (onVideosChange) {
        await onVideosChange(updatedVideos)
      }

      bar.success('Video deleted from gallery and server successfully')
      setDeleteConfirmOpen(false)
      setVideoToDelete(null)
    } catch (error) {
      console.error('Failed to delete video:', error)
      bar.error(error.message || 'Failed to delete video from gallery')
    } finally {
      setDeletingVideoId(null)
    }
  }

  /**
   * Unlink a video from the gallery after confirmation
   * This will remove the video from the list but NOT delete it from the server
   */
  const handleUnlinkConfirm = async () => {
    if (!videoToUnlink) return

    const { index } = videoToUnlink

    try {
      const updatedVideos = videos.filter((_, i) => i !== index)

      if (onVideosChange) {
        await onVideosChange(updatedVideos)
      }

      bar.success('Video unlinked from gallery successfully')
      setUnlinkConfirmOpen(false)
      setVideoToUnlink(null)
    } catch (error) {
      console.error('Failed to unlink video:', error)
      bar.error(error.message || 'Failed to unlink video from gallery')
    }
  }

  /**
   * Open attach existing videos dialog
   */
  const handleOpenAttachDialog = async () => {
    setOpenAttachDialog(true)
    setSelectedExistingVideos([])
    setLoadingExistingVideos(true)

    try {
      const response = await VideoService.getVideos({ ownerId: user.resourceId })
      setExistingVideos(response || [])
    } catch (error) {
      console.error('Failed to load existing videos:', error)
      bar.error(error.message || 'Failed to load existing videos')
    } finally {
      setLoadingExistingVideos(false)
    }
  }

  /**
   * Close attach existing videos dialog
   */
  const handleCloseAttachDialog = () => {
    setOpenAttachDialog(false)
    setSelectedExistingVideos([])
    setExistingVideos([])
  }

  /**
   * Toggle selection of an existing video
   */
  const handleToggleExistingVideo = (video) => {
    setSelectedExistingVideos((prev) => {
      const isSelected = prev.some((v) => v.resourceId === video.resourceId)
      if (isSelected) {
        return prev.filter((v) => v.resourceId !== video.resourceId)
      } else {
        return [...prev, video]
      }
    })
  }

  /**
   * Attach selected existing videos to gallery
   */
  const handleAttachExistingVideos = async () => {
    if (selectedExistingVideos.length === 0) {
      bar.warn('Please select at least one video to attach')
      return
    }

    setAttachingVideos(true)

    try {
      const newVideos = selectedExistingVideos.map((video) => ({
        resourceId: video.resourceId,
        url: video.r2Url || video.url,
        r2Url: video.r2Url,
        coverImageUrl: video.coverImageUrl,
      }))

      // Filter out duplicates based on resourceId
      const existingResourceIds = new Set(videos.filter((v) => typeof v === 'object' && v.resourceId).map((v) => v.resourceId))

      const uniqueNewVideos = newVideos.filter((v) => !existingResourceIds.has(v.resourceId))

      if (uniqueNewVideos.length === 0) {
        bar.warn('All selected videos are already in the gallery')
        setAttachingVideos(false)
        return
      }

      const updatedVideos = [...videos, ...uniqueNewVideos]

      if (onVideosChange) {
        await onVideosChange(updatedVideos)
      }

      bar.success(`${uniqueNewVideos.length} video(s) attached to gallery successfully`)
      handleCloseAttachDialog()
    } catch (error) {
      console.error('Failed to attach videos:', error)
      bar.error(error.message || 'Failed to attach videos to gallery')
    } finally {
      setAttachingVideos(false)
    }
  }

  /**
   * Handle video click to open fullscreen viewer
   */
  const handleVideoClick = (index) => {
    setViewerInitialIndex(index)
    setViewerOpen(true)
  }

  /**
   * Handle close fullscreen viewer
   */
  const handleCloseViewer = () => {
    setViewerOpen(false)
  }

  return (
    <Box>
      {normalizedVideos.length > 0 ? (
        <Grid container spacing={2}>
          {normalizedVideos.map((video, index) => (
            <Grid key={video.id} size={{ xs: 12, sm: 6, md: 4 }}>
              <Card>
                <CardMedia
                  component="img"
                  height="200"
                  image={video.coverUrl || '/placeholder-video.png'}
                  alt={`Video ${index + 1}`}
                  onClick={() => handleVideoClick(index)}
                  sx={{ objectFit: 'cover', cursor: 'pointer' }}
                />
                {editable && (
                  <CardActions sx={{ justifyContent: 'flex-end', gap: 0.5 }}>
                    <IconButton
                      size="small"
                      onClick={() => handleUnlinkClick(video, index)}
                      title="Unlink (remove from gallery but keep on server)"
                      sx={{
                        '&:hover': {
                          bgcolor: 'warning.main',
                          color: 'white',
                        },
                      }}
                    >
                      <LinkOffIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => handleDeleteClick(video, index)}
                      disabled={deletingVideoId === video.id}
                      title="Delete (remove from gallery and delete from server)"
                      sx={{
                        '&:hover': {
                          bgcolor: 'error.main',
                          color: 'white',
                        },
                      }}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </CardActions>
                )}
              </Card>
            </Grid>
          ))}
        </Grid>
      ) : (
        <Card variant="outlined" sx={{ p: 3, textAlign: 'center', bgcolor: 'action.hover' }}>
          <Typography variant="body2" color="text.secondary">
            No videos yet. {editable && 'Click "Upload Videos" to upload new videos or "Attach Videos" to link existing ones.'}
          </Typography>
        </Card>
      )}

      {editable && (
        <CardActions sx={{ justifyContent: 'center', mt: 2, gap: 1 }}>
          <Button variant="outlined" startIcon={<CloudUploadIcon />} onClick={handleOpenUploadDialog}>
            Upload Videos
          </Button>
          <Button variant="outlined" startIcon={<AttachFileIcon />} onClick={handleOpenAttachDialog}>
            Attach Videos
          </Button>
        </CardActions>
      )}

      {/* Hidden canvas for poster generation */}
      <canvas ref={canvasRef} style={{ display: 'none' }} />

      {/* Upload Dialog */}
      <Dialog open={openUploadDialog} onClose={handleCloseUploadDialog} maxWidth="md" fullWidth>
        <DialogTitle>Upload Videos to Gallery</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 1 }}>
            {uploadedVideos.length > 0 ? (
              // Show uploaded videos preview
              <Box>
                <Typography variant="body2" color="success.main" sx={{ mb: 2 }}>
                  ✓ {uploadedVideos.length} video(s) uploaded successfully
                </Typography>
                <Grid container spacing={2}>
                  {uploadedVideos.map((videoData, index) => (
                    <Grid key={index} size={{ xs: 6, sm: 4 }}>
                      <Card>
                        <CardMedia
                          component="img"
                          height="140"
                          image={videoData.coverImageUrl || '/placeholder-video.png'}
                          alt={`Uploaded ${index + 1}`}
                        />
                      </Card>
                    </Grid>
                  ))}
                </Grid>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 2 }}>
                  Click "Confirm" below to add these videos to your gallery, or "Cancel" to remove them.
                </Typography>
              </Box>
            ) : selectedFiles.length > 0 ? (
              // Show selected files preview
              <Box>
                <Typography variant="body2" color="primary" sx={{ mb: 2 }}>
                  {selectedFiles.length} video(s) selected. Click "Upload" to upload them.
                </Typography>
                <Grid container spacing={2} sx={{ mb: 2 }}>
                  {selectedFiles.map((fileItem) => (
                    <Grid key={fileItem.id} size={{ xs: 6, sm: 4 }}>
                      <Box sx={{ position: 'relative' }}>
                        <Card>
                          <CardMedia
                            component="img"
                            height="140"
                            image={fileItem.posterUrl}
                            alt={fileItem.file.name}
                            sx={{ cursor: 'pointer' }}
                            onClick={() => handleOpenVideoPreview(fileItem)}
                          />
                          <Box
                            sx={{
                              position: 'absolute',
                              top: '50%',
                              left: '50%',
                              transform: 'translate(-50%, -50%)',
                              bgcolor: 'rgba(0, 0, 0, 0.5)',
                              borderRadius: '50%',
                              p: 1,
                              cursor: 'pointer',
                            }}
                            onClick={() => handleOpenVideoPreview(fileItem)}
                          >
                            <PlayArrowIcon sx={{ color: 'white', fontSize: 32 }} />
                          </Box>
                        </Card>
                        <IconButton
                          size="small"
                          sx={{
                            position: 'absolute',
                            top: 4,
                            right: 4,
                            bgcolor: 'background.paper',
                            '&:hover': { bgcolor: 'error.main', color: 'white' },
                          }}
                          onClick={() => handleRemoveSelectedFile(fileItem.id)}
                        >
                          <CancelIcon fontSize="small" />
                        </IconButton>
                        <Typography variant="caption" noWrap sx={{ display: 'block', mt: 1 }}>
                          {fileItem.file.name}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                          Click to change poster frame
                        </Typography>
                      </Box>
                    </Grid>
                  ))}
                </Grid>
              </Box>
            ) : (
              // Show drag and drop area
              <Paper
                variant="outlined"
                onDragEnter={handleDragEnter}
                onDragLeave={handleDragLeave}
                onDragOver={handleDragOver}
                onDrop={handleDrop}
                sx={{
                  p: 4,
                  textAlign: 'center',
                  cursor: 'pointer',
                  bgcolor: isDragging ? 'action.hover' : 'background.paper',
                  borderStyle: 'dashed',
                  borderWidth: 2,
                  borderColor: isDragging ? 'primary.main' : 'divider',
                  transition: 'all 0.2s',
                }}
                onClick={() => fileInputRef.current?.click()}
              >
                <CloudUploadIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
                <Typography variant="body1" gutterBottom>
                  Drag and drop videos here, or click to select
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Supported formats: MP4, MOV, AVI, WEBM (max 500MB each)
                </Typography>
                <Typography variant="caption" color="primary" sx={{ display: 'block', mt: 1 }}>
                  Poster images will be generated automatically
                </Typography>
              </Paper>
            )}

            <input ref={fileInputRef} type="file" accept="video/mp4,video/quicktime,video/x-msvideo,video/webm" multiple style={{ display: 'none' }} onChange={handleFileInputChange} />
          </Box>
        </DialogContent>
        <DialogActions>
          {uploadedVideos.length > 0 ? (
            <>
              <Button onClick={handleCancelAllUploads} disabled={isConfirming}>
                Cancel
              </Button>
              <Button onClick={handleConfirmUpload} variant="contained" disabled={isConfirming}>
                {isConfirming ? 'Adding...' : 'Confirm'}
              </Button>
            </>
          ) : selectedFiles.length > 0 ? (
            <>
              <Button onClick={handleCloseUploadDialog} disabled={isUploading}>
                Close
              </Button>
              <Button onClick={handleUploadFiles} variant="contained" startIcon={<CloudUploadIcon />} disabled={isUploading}>
                {isUploading ? (
                  <>
                    <CircularProgress size={20} sx={{ mr: 1 }} />
                    Uploading...
                  </>
                ) : (
                  `Upload ${selectedFiles.length} video(s)`
                )}
              </Button>
            </>
          ) : (
            <Button onClick={handleCloseUploadDialog}>Close</Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Video Preview Dialog for Poster Selection */}
      <Dialog open={currentVideoPreview !== null} onClose={() => setCurrentVideoPreview(null)} maxWidth="md" fullWidth>
        <DialogTitle>Select Poster Frame</DialogTitle>
        <DialogContent>
          {currentVideoPreview && (
            <Box>
              <video
                ref={videoRef}
                src={currentVideoPreview.previewUrl}
                style={{ width: '100%', borderRadius: 8 }}
                muted
              />
              <Box sx={{ mt: 2, px: 2 }}>
                <Typography variant="caption" gutterBottom>
                  Scrub through the video to select the poster frame:
                </Typography>
                <Slider
                  value={currentVideoPreview.currentTime}
                  onChange={handlePosterTimeChange}
                  min={0}
                  max={currentVideoPreview.duration}
                  step={0.1}
                  valueLabelDisplay="auto"
                  valueLabelFormat={(value) => `${value.toFixed(1)}s`}
                />
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCurrentVideoPreview(null)} variant="contained">
            Done
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteConfirmOpen} onClose={handleDeleteCancel} maxWidth="xs" fullWidth>
        <DialogTitle>Delete Video</DialogTitle>
        <DialogContent>
          <Typography variant="body1" gutterBottom>
            Are you sure you want to delete this video?
          </Typography>
          <Typography variant="body2" color="text.secondary">
            <strong>Delete</strong> will remove it from the videos list and delete it from the server. This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel} disabled={deletingVideoId !== null}>
            Cancel
          </Button>
          <Button onClick={handleDeleteConfirm} variant="contained" color="error" disabled={deletingVideoId !== null}>
            {deletingVideoId !== null ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Unlink Confirmation Dialog */}
      <Dialog open={unlinkConfirmOpen} onClose={handleUnlinkCancel} maxWidth="xs" fullWidth>
        <DialogTitle>Unlink Video</DialogTitle>
        <DialogContent>
          <Typography variant="body1" gutterBottom>
            Are you sure you want to unlink this video?
          </Typography>
          <Typography variant="body2" color="text.secondary">
            <strong>Unlink</strong> will remove it from the videos list but will not delete it from the server. You can add it back later if needed.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleUnlinkCancel}>Cancel</Button>
          <Button onClick={handleUnlinkConfirm} variant="contained" color="warning">
            Unlink
          </Button>
        </DialogActions>
      </Dialog>

      {/* Attach Existing Videos Dialog */}
      <Dialog open={openAttachDialog} onClose={handleCloseAttachDialog} maxWidth="md" fullWidth>
        <DialogTitle>Attach Existing Videos</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 1 }}>
            {loadingExistingVideos ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                <CircularProgress />
              </Box>
            ) : existingVideos.length > 0 ? (
              <>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Select videos to attach to this gallery. {selectedExistingVideos.length > 0 && `(${selectedExistingVideos.length} selected)`}
                </Typography>
                <Grid container spacing={2}>
                  {existingVideos.map((video) => {
                    const isSelected = selectedExistingVideos.some((v) => v.resourceId === video.resourceId)
                    const isAlreadyInGallery = videos.some((v) => typeof v === 'object' && v.resourceId === video.resourceId)

                    return (
                      <Grid key={video.resourceId} size={{ xs: 6, sm: 4 }}>
                        <Card
                          sx={{
                            cursor: isAlreadyInGallery ? 'not-allowed' : 'pointer',
                            opacity: isAlreadyInGallery ? 0.5 : 1,
                            border: isSelected ? 3 : 0,
                            borderColor: 'primary.main',
                          }}
                          onClick={() => !isAlreadyInGallery && handleToggleExistingVideo(video)}
                        >
                          <CardMedia
                            component="img"
                            height="140"
                            image={video.coverImageUrl || '/placeholder-video.png'}
                            alt={`Video ${video.resourceId}`}
                          />
                          <CardActions sx={{ justifyContent: 'space-between' }}>
                            {isAlreadyInGallery ? (
                              <Typography variant="caption" sx={{ fontStyle: 'italic' }}>
                                Already in gallery
                              </Typography>
                            ) : (
                              <Checkbox checked={isSelected} icon={<CheckBoxOutlineBlankIcon />} checkedIcon={<CheckBoxIcon />} />
                            )}
                          </CardActions>
                        </Card>
                      </Grid>
                    )
                  })}
                </Grid>
              </>
            ) : (
              <Card variant="outlined" sx={{ p: 3, textAlign: 'center', bgcolor: 'action.hover' }}>
                <Typography variant="body2" color="text.secondary">
                  No existing videos found. Upload new videos first.
                </Typography>
              </Card>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseAttachDialog} disabled={attachingVideos}>
            Cancel
          </Button>
          <Button onClick={handleAttachExistingVideos} variant="contained" disabled={selectedExistingVideos.length === 0 || attachingVideos}>
            {attachingVideos ? (
              <>
                <CircularProgress size={20} sx={{ mr: 1 }} />
                Attaching...
              </>
            ) : (
              `Attach ${selectedExistingVideos.length > 0 ? selectedExistingVideos.length : ''} Video${selectedExistingVideos.length !== 1 ? 's' : ''}`
            )}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Fullscreen Media Viewer */}
      <FullscreenMediaViewer
        open={viewerOpen}
        onClose={handleCloseViewer}
        media={normalizedVideos.map((vid) => ({
          type: 'video',
          url: vid.url,
          coverUrl: vid.coverUrl || '/placeholder-video.png',
        }))}
        initialIndex={viewerInitialIndex}
      />
    </Box>
  )
}

export default VideoManagement
